import isString from 'lodash/isString';
import some from 'lodash/some';
import split from 'lodash/split';
import { matchPath } from 'react-router-dom';

/**
 * The single source of truth for which routes require a full subscription.
 *
 * Two consumers read this list and MUST agree, or free-tier users see nav
 * entries that bounce (or locks on pages they can actually open):
 *   - routes/PrivateRoute — gates the route itself, looking its own `path` up
 *     by exact pattern.
 *   - components/nav-apps, components/nav-agents — decide whether a sidebar
 *     entry renders locked, resolving a concrete URL through matchPath.
 *
 * Entries are route PATTERNS exactly as declared in routes/index.tsx (react
 * router v5 syntax, so ':id' and optional ':id?' are both fine).
 *
 * Adding a FullAccess route means adding it here. A route left out of this
 * list falls back to BasicAccess — the same failure mode as forgetting the
 * old inline `role={Role.FullAccess}` prop, just in one place now.
 */
export const FULL_ACCESS_ROUTES = [
  '/',
  '/cot',
  '/cot-test',
  '/customize',
  '/daily-reports',
  '/daily-reports/:id',
  '/dashboard',
  '/dashboard/:id',
  '/dashboard/:id/:slug',
  '/fundamentals-summary',
  '/geopolitics',
  '/global-flows',
  '/global-flows-powerbi',
  '/hormuz-flows',
  '/global-margins',
  '/open-interest',
  '/refinery-margins',
  '/scheduled-tasks',
  '/tanker-data-hub',
  '/unit-margins',
];

const FULL_ACCESS_ROUTE_SET = new Set(FULL_ACCESS_ROUTES);

/** For a route DECLARATION: is this exact pattern a FullAccess route? */
export function isFullAccessRoutePattern (pattern?: string | string[]) {
  if (!isString(pattern)) return false;
  return FULL_ACCESS_ROUTE_SET.has(pattern);
}

/**
 * For a concrete URL (a nav target like '/dashboard/398?pageName=abc'): does
 * opening it require full access?
 *
 * Resolution goes through react-router's own matchPath rather than a
 * hand-rolled comparison, so a nav URL resolves to exactly the route the
 * router would pick — including optional params ('/chat' vs '/chat/:id?')
 * and patterns with multiple segments.
 */
export function requiresFullAccess (url: string) {
  const pathname = split(url, '?')[0];
  return some(FULL_ACCESS_ROUTES, (pattern) =>
    Boolean(matchPath(pathname, { path: pattern, exact: true }))
  );
}
